import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {Publisher} from '@app/core/models/product.model';
import {MessageService, NotificationTypes} from '@app/core/services/message.service';
import {StaticDataCacheService} from '@app/order/services/static-data-cache.service';
import {Message} from '@core/models/message.model';
import {BehaviorSubject, EMPTY, expand, Observable, of, scan, switchMap} from 'rxjs';
import {catchError, finalize} from "rxjs/operators";
import { NavigationExtras, Router } from '@angular/router';

@Component({
	selector: 'app-notifications-list',
	templateUrl: './notifications-list.component.html',
	styleUrls: ['./notifications-list.component.css'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsListComponent implements OnInit {

	private readonly MAX_PAGES = 1000;
	private readonly NOTIFICATION_PAGE_SIZE = 5;

	@Input()
	get showNewSinceLastLogin() { return this._showNewSinceLastLogin }
	set showNewSinceLastLogin(value: any) {
		this._showNewSinceLastLogin = (value != null && `${value}` !== 'false');
	}
	private _showNewSinceLastLogin: boolean = false;

	constructor(
		private _messageService: MessageService,
		private _router: Router,
		private _staticDataCacheService: StaticDataCacheService
	) { }

	NotificationTypes = NotificationTypes; // expose enum to template

	notifications$?: Observable<Message[]>;
	readonly callingOut$ = new BehaviorSubject<boolean>(false);


	private _publishers: Publisher[] = [];

	ngOnInit(): void {
		this.notifications$ = this._staticDataCacheService.publishers$
			.pipe(
				switchMap(publishers => {
					this._publishers = publishers;
					return this._loadNotifications(this._showNewSinceLastLogin);
				})
			)
		;
	}

	public getCompanyNameById(companyId: string): string {
		return this._publishers?.find(x => x.id === companyId)?.label ?? "";
	}

	goToOrder(notification: Message) {
		this._messageService.markMessageAsRead(notification.id);

		const navigationExtras: NavigationExtras = {
			queryParams: {
				orderId: notification.orderId
			},
			state: {
				triggeringNotificationMessage: notification
			}
		};

		this._router.navigate(['/order'], navigationExtras);
	}

	private _loadNotifications(sinceLastLogin: boolean): Observable<Message[]> {
		let pageNo = 1;
		this.callingOut$.next(true);

		const getNotifications = sinceLastLogin
			? (page: number) =>
				this._messageService.getNotificationsSinceLastLogin(page)

			: (page: number, size: number) =>
				this._messageService.getAllNotifications(page, size);


		return getNotifications(pageNo, this.NOTIFICATION_PAGE_SIZE).pipe(
			expand((messages) => {
				pageNo++;
				const shouldLoadMore = messages.length === this.NOTIFICATION_PAGE_SIZE && pageNo <= this.MAX_PAGES;
				return shouldLoadMore ? getNotifications(pageNo, this.NOTIFICATION_PAGE_SIZE) : EMPTY;
			}),
			scan((acc: Message[], messages) => [...acc, ...messages], [] as Message[]),
			catchError((error) => {
				console.error(`Error in loadNotifications (sinceLastLogin=${sinceLastLogin}):`, error);
				this.callingOut$.next(false);
				return of([]);
			}),
			finalize(() => {
				this.callingOut$.next(false);
			}),
		)
	}
}
